import {IBookingInfoAtTime} from 'external-services/booking-availability-service/types';
import {BookingAvailabilityService} from 'external-services/booking-availability-service';
import {cloneDeep} from 'lodash';
import {BookingMethods, IBookingTime, IInfoAtTime, ITableItem} from 'shared-types/SharedTypes';
import {DiaryRootState} from 'appRoot/rootReducer';
import TimeSelectionService from 'booking-creator/services/time-selection-service';
import DateUtilsService from 'external-services/date-utils-service';
import {TimeMode} from 'booking-creator/components/TimeSelector/types';
import {bookingTimesActions} from "booking-creator/reducers/bookingTimes/slice";
import {AppDispatch} from "appRoot/Store";

const NS = 'BookingTimesSliceHelpers';

/**
 * May need this for checking isInList & seatNowTime for walkins
 */
// export function getSelectedInfoAtTimeWithUpdatedAvailability(
//   state: DiaryMobileState,
//   callee: string
// ): IBookingTime {
//   const {
//     displayReducer: { activeBooking, selectedService, activeSchedule, isDateToday },
//     activeLayoutsReducer: { layouts },
//     activeSectionsReducer: { bookingsByTable, tablesListForActiveService },
//     venueReducer: { timeZoneId },
//   } = state;
//
//   const selectedInfoAtTimeNoTable: IBookingInfoAtTime =
//     BookingAvailabilityService.checkBookingsAtTime(
//       BookingAvailabilityService.setSeatNowTime(isDateToday, timeZoneId),
//       activeBooking.time,
//       activeSchedule,
//       selectedService as IServiceItemSubset,
//       cloneDeep(layouts), // gets modified, so needs to be cloned
//       activeBooking.duration as number,
//       activeBooking.people,
//       BookingAvailabilityService.getMutableTables(tablesListForActiveService),
//       BookingAvailabilityService.getMutableBookingsByTable(bookingsByTable),
//       activeBooking.preferredSectionId,
//       null,
//       true,
//       activeBooking.method as BookingMethods
//     );
//
//   console.log(NS, callee, 'selectedInfoAtTimeNoTable', selectedInfoAtTimeNoTable);
//
//   return selectedInfoAtTimeNoTable as IBookingTime;
// }

export async function getSelectedInfoAtTimeForNewBooking(
  state: DiaryRootState,
  dispatch: AppDispatch,
  availableTimes: IBookingInfoAtTime[],
  tablesMutable: ITableItem[],
  keepSelectedTime: boolean
): Promise<{ selectedInfoAtTime: IBookingTime; selectedTableIds: string[] }> {
  const {
    displayReducer: { activeBooking, selectedService },
    tablesReducer: { hasUserDefinedTables },
    bookingTimesReducer: { timeSelectorMode, selectedInfoAtTime },
    scheduleReducer: { selectedDate },
    venueReducer: {timeZoneId},
  } = state;

  const hasTableSelection = !!activeBooking.tables.length;

  /**
   * selectedTime defaults to first time in selected service.
   * But if the booking time is within the service start/end times, it tries to match it as close as possible.
   */
  let selectedTime: IInfoAtTime = availableTimes[0] || null;

  const defaultTime = activeBooking.time || selectedDate
  // first, we only care if it is for "today"
  const isDateToday = DateUtilsService.isDateToday(defaultTime, timeZoneId, true);
  if (isDateToday) {

    /**
     * @todo: after midnight the selectedService if for the venue time, but when using floorplan, it needs to be
     * the selected time.
     */

    const normalisedDate = DateUtilsService.normaliseDateWhenVenueTimeIsPastMidnight(defaultTime, timeZoneId);
    const serviceStart = DateUtilsService.normaliseDateWhenVenueTimeIsPastMidnight(selectedService.startTime as Date, timeZoneId);
    const serviceEnd = DateUtilsService.normaliseDateWhenVenueTimeIsPastMidnight(selectedService.endTime as Date, timeZoneId);

    /**
     * if it has a table selection already, it means it was selected from the floorplan and so we can use it's time,
     * otherwise we only care if the current venue time is during the selected service
     */
    const currentTime = (hasTableSelection || keepSelectedTime)
      ? normalisedDate
      : DateUtilsService.getVenueTime(timeZoneId);
    const isCurrentTimeWithinService = DateUtilsService.isInBetweenDates(currentTime, serviceStart, serviceEnd);
    if (isCurrentTimeWithinService) {

      if (keepSelectedTime) {
        selectedTime = TimeSelectionService.selectNearestTime(
          defaultTime,
          selectedDate,
          availableTimes
        );
      } else {

        selectedTime = TimeSelectionService.selectNearestFutureTime(
          currentTime,
          selectedDate,
          availableTimes,
          true
        );

        // if no future times available, we revert to using the closest time (even though it could be in the past)
        if (!selectedTime) {
          selectedTime = TimeSelectionService.selectNearestTime(
            defaultTime,
            selectedDate,
            availableTimes
          );
        }
      }


    }

    const isManualTimeMode = timeSelectorMode === TimeMode.manual;

    let isManualTimeWithinService = false;
    if (isManualTimeMode && selectedTime) {
      isManualTimeWithinService = DateUtilsService.isInBetweenDates(normalisedDate, serviceStart, serviceEnd);
      if (isManualTimeWithinService) {
        selectedTime.time = defaultTime;
        selectedTime.isManualTime = true;
      } else {
        dispatch(bookingTimesActions.setTimeSelectorMode(TimeMode.predefined));
      }
    }
  } else if (keepSelectedTime) {
    selectedTime = TimeSelectionService.selectNearestTime(
      defaultTime,
      selectedDate,
      availableTimes
    );
  }

  let newSelectedInfoAtTime: IBookingTime;
  let selectedTableIds: string[] = [];
  try {
    const tableIds = activeBooking.tables.map(i => i._id)
    const {
      newSelectedTime,
      previouslySelectedInfoAtTime,
      newSelectedTablesList,
      availableTablesList,
    } = await TimeSelectionService.addTableToSelectedInfoAtTime({
      selectedTime: selectedTime.time,
      selectedInfoAtTime: { ...selectedTime, table: null },
      hasUserDefinedTables,
      autoSelectTable: true,
      activeBookingMethod: activeBooking.method,
      activeBookingType: activeBooking.bookingType,
      activePax: activeBooking.people,
      tablesList: tablesMutable,
      /**
       * @todo: for edit booking this only currently supports selecting the first table, but it should select multiple
       */
      tableIds, // will always be empty for Diary Mobile because there is no floorplan or timeline to select table
      isCustomTime: false, // used for walkins that can enter a custom time. @todo: get this working when doing walkin logic
      isEditBooking: false,
    });
    newSelectedInfoAtTime = newSelectedTime;
    selectedTableIds = activeBooking.time ? availableTablesList.map((t) => t._id) : [];
  } catch (err) {
    console.warn(
      NS,
      'getBookingTimes',
      `failed to get newSelectedTime on 'TimeSelectionService.addTableToSelectedInfoAtTime'`,
      err
    );
  }

  return { selectedInfoAtTime: newSelectedInfoAtTime, selectedTableIds };
}

export function getSelectedInfoAtTimeForEditBooking(
  state: DiaryRootState,
  availableTimes: IBookingInfoAtTime[],
  tablesMutable: ITableItem[]
): { selectedInfoAtTime: IBookingTime; selectedTableIds: string[]; isNotInList: boolean } {
  const {
    displayReducer: { activeBooking, selectedService, activeSchedule, isDateToday, mode },
    activeLayoutsReducer: { layouts },
    activeSectionsReducer: { bookingsByTable, tablesListForActiveService },
    venueReducer: { timeZoneId },
    scheduleReducer: { selectedDate },
  } = state;

  // @todo: look into isInList logic. Might need it.

  const selectedBookingTimeFromList = BookingAvailabilityService.getSelectedBookingTimeFromList(
    availableTimes,
    activeBooking.time || DateUtilsService.getVenueTime(timeZoneId)
  );

  /**
   * @todo: may need to handle -1 index for selectedBookingTimeFromList.selectedTimeId
   */
  let isNotInList: boolean = false;
  let selectedInfoAtTime: IBookingTime = selectedBookingTimeFromList.selectedTime as IBookingTime;
  if (!selectedInfoAtTime) {
    selectedInfoAtTime = getSelectedInfoAtTime(state);
    isNotInList = true;
  }
  const selectedTableIds: string[] = activeBooking.tables
    ? activeBooking.tables.map((t) => t._id)
    : [];

  return { selectedInfoAtTime, selectedTableIds, isNotInList };
}

export function getSelectedInfoAtTime(state: DiaryRootState): IBookingTime {
  const {
    displayReducer: { activeBooking, activeSchedule, selectedService },
    activeSectionsReducer: { tablesListForActiveService, bookingsByTable },
    activeLayoutsReducer: { layouts },
    venueReducer: { timeZoneId },
  } = state;
  const defaultTime = activeBooking.time || DateUtilsService.getVenueTime(timeZoneId)
  return BookingAvailabilityService.checkBookingsAtTime(
    defaultTime,
    defaultTime,
    activeSchedule,
    selectedService as any,
    cloneDeep(layouts),
    activeBooking.duration as number,
    activeBooking.people,
    BookingAvailabilityService.getMutableTables(tablesListForActiveService),
    BookingAvailabilityService.getMutableBookingsByTable(bookingsByTable),
    activeBooking.preferredSectionId,
    null,
    true,
    activeBooking.method as BookingMethods
  ) as IBookingTime;
}
